/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { useContext } from "react";

import { AccountContext, AccountContextType } from "./AccountProvider";

export interface Account {
	isLoggedIn?: boolean;
	country?: string;
	displayName?: string;
	email?: string;
	id?: string;
	preferredLanguage?: string;
	cabinedMode?: boolean;
}

export const useAccountContext = (): AccountContextType => {
	const context = useContext(AccountContext);
	if (context === undefined) {
		throw new Error("useAccountContext must be used within an AccountProvider");
	}
	return context;
};

export const useAccountStore = (): {
	isLoading: boolean;
	account: Account;
} => {
	const { account, isLoading } = useAccountContext();
	return { account, isLoading };
};

export const useIsLoggedIn = (): boolean => {
	const { account } = useAccountStore();
	return account?.isLoggedIn || false;
};

export const useAccountEmail = (): string => {
	const { account } = useAccountStore();
	return account?.email || "";
};

export const useAccountId = (): string => {
	const { account } = useAccountStore();
	return account?.id || "";
};

export const useAccountCountry = (): string => {
	const { account } = useAccountStore();
	return account?.country || "";
};
